import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle,
  DownloadSimple,
  FileText,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  Repeat,
  Trash,
  TrendDown,
  TrendUp,
} from '@phosphor-icons/react';
import { Layout } from '../components/layout/Layout';
import {
  createSubscription,
  deleteSubscription,
  generateSubscriptionQuotation,
  getClients,
  getProjects,
  getSubscriptions,
  updateSubscription,
} from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

const transactionTypes = [
  { value: 'soporte', label: 'Soporte' },
  { value: 'suscripcion', label: 'Suscripción' },
  { value: 'licencia', label: 'Licencia' },
  { value: 'mantenimiento', label: 'Mantenimiento' },
  { value: 'hosting', label: 'Hosting' },
  { value: 'otro', label: 'Otro' },
];

const frequencyLabels = {
  monthly: 'Mensual',
  quarterly: 'Trimestral',
  biannual: 'Semestral',
  annually: 'Anual',
};

const statusLabels = {
  active: 'Activa',
  paused: 'Pausada',
  cancelled: 'Cancelada',
};

const emptyForm = () => ({
  client_id: 'none',
  project_id: 'none',
  service_name: '',
  provider_name: '',
  description: '',
  transaction_type: 'otro',
  billing_frequency: 'monthly',
  start_date: new Date().toISOString().slice(0, 10),
  billing_day: 1,
  client_rate: 0,
  provider_cost: 0,
  license_quantity: 1,
  client_unit_price: 0,
  provider_unit_cost: 0,
  scope_items: [],
  status: 'active',
  last_generated_billing_period: '',
});

const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const numberValue = (value) => Number(value || 0);

const monthlyEquivalent = (subscription, field) => {
  const value = Number(subscription[field] || 0);
  if (subscription.billing_frequency === 'quarterly') return value / 3;
  if (subscription.billing_frequency === 'biannual') return value / 6;
  if (subscription.billing_frequency === 'annually') return value / 12;
  return value;
};

const getSubscriptionId = (subscription) => subscription.subscription_id || subscription._id;

const formatLocalDateOnly = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getRecurringGenerationGateDate = (subscription) => {
  const period = subscription.last_generated_billing_period;
  if (!period || !/^\d{4}-\d{2}$/.test(period)) return null;

  const [year, month] = period.split('-').map(Number);
  const billingDay = Math.min(Math.max(Number(subscription.billing_day || 1), 1), 31);
  const daysInMonth = new Date(year, month, 0).getDate();
  const day = Math.min(billingDay, daysInMonth);

  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const canGenerateRecurringQuotation = (subscription, now = new Date()) => {
  const gateDate = getRecurringGenerationGateDate(subscription);
  if (!gateDate) return true;
  return formatLocalDateOnly(now) > gateDate;
};

const getGenerateQuotationBlockedText = (subscription) => {
  const gateDate = getRecurringGenerationGateDate(subscription);
  if (!gateDate || canGenerateRecurringQuotation(subscription)) return '';
  return `Disponible después del ${gateDate}`;
};

export default function SubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState(emptyForm);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;

      const [subscriptionsRes, clientsRes, projectsRes] = await Promise.all([
        getSubscriptions(params),
        getClients().catch(() => ({ data: [] })),
        getProjects().catch(() => ({ data: [] })),
      ]);

      setSubscriptions(subscriptionsRes.data || []);
      setClients(clientsRes.data || []);
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar transacciones recurrentes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredSubscriptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return subscriptions;

    return subscriptions.filter((subscription) => {
      const searchable = [
        subscription.service_name,
        subscription.provider_name,
        subscription.client_name,
        subscription.project_name,
        subscription.description,
        ...(subscription.scope_items || []),
        subscription.transaction_type,
        subscription.last_generated_billing_period,
        subscription.last_generated_quotation_number,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(term);
    });
  }, [subscriptions, searchTerm]);

  const metrics = useMemo(() => {
    const active = subscriptions.filter((subscription) => subscription.status === 'active');
    const monthlyRevenue = active.reduce((sum, subscription) => sum + monthlyEquivalent(subscription, 'client_rate'), 0);
    const monthlyCosts = active.reduce((sum, subscription) => sum + monthlyEquivalent(subscription, 'provider_cost'), 0);

    return {
      activeCount: active.length,
      monthlyRevenue,
      monthlyCosts,
      monthlyMargin: monthlyRevenue - monthlyCosts,
    };
  }, [subscriptions]);

  const openCreateDialog = () => {
    setEditingSubscription(null);
    setFormData(emptyForm());
    setIsFormOpen(true);
  };

  const openEditDialog = (subscription) => {
    setEditingSubscription(subscription);
    setFormData({
      client_id: subscription.client_id || 'none',
      project_id: subscription.project_id || 'none',
      service_name: subscription.service_name || '',
      provider_name: subscription.provider_name || '',
      description: subscription.description || '',
      transaction_type: subscription.transaction_type || 'otro',
      billing_frequency: subscription.billing_frequency || 'monthly',
      start_date: (subscription.start_date || '').slice(0, 10),
      billing_day: subscription.billing_day || 1,
      client_rate: subscription.client_rate || 0,
      provider_cost: subscription.provider_cost || 0,
      license_quantity: subscription.license_quantity || 1,
      client_unit_price: subscription.client_unit_price ?? subscription.client_rate ?? 0,
      provider_unit_cost: subscription.provider_unit_cost ?? subscription.provider_cost ?? 0,
      scope_items: subscription.scope_items || [],
      status: subscription.status || 'active',
      last_generated_billing_period: subscription.last_generated_billing_period || '',
    });
    setIsFormOpen(true);
  };

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const updateScopeItem = (index, value) => {
    setFormData((current) => ({
      ...current,
      scope_items: current.scope_items.map((item, itemIndex) => (itemIndex === index ? value : item)),
    }));
  };

  const addScopeItem = () => {
    setFormData((current) => ({
      ...current,
      scope_items: [...current.scope_items, ''],
    }));
  };

  const removeScopeItem = (index) => {
    setFormData((current) => ({
      ...current,
      scope_items: current.scope_items.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const isLicenseForm = formData.transaction_type === 'licencia';
  const licenseQuantity = Math.max(Number.parseInt(formData.license_quantity || 1, 10), 1);
  const licenseClientTotal = licenseQuantity * numberValue(formData.client_unit_price);
  const licenseProviderTotal = licenseQuantity * numberValue(formData.provider_unit_cost);

  const buildPayload = () => {
    const isLicense = formData.transaction_type === 'licencia';
    const quantity = Math.max(Number.parseInt(formData.license_quantity || 1, 10), 1);
    const clientUnitPrice = numberValue(formData.client_unit_price);
    const providerUnitCost = numberValue(formData.provider_unit_cost);
    const clientRate = isLicense ? quantity * clientUnitPrice : numberValue(formData.client_rate);
    const providerCost = isLicense ? quantity * providerUnitCost : numberValue(formData.provider_cost);

    return {
      client_id: formData.client_id === 'none' ? '' : formData.client_id,
      project_id: formData.project_id === 'none' ? '' : formData.project_id,
      service_name: formData.service_name.trim(),
      provider_name: formData.provider_name.trim(),
      description: formData.description.trim(),
      transaction_type: formData.transaction_type,
      billing_frequency: formData.billing_frequency,
      start_date: formData.start_date,
      billing_day: Number(formData.billing_day || 1),
      client_rate: clientRate,
      provider_cost: providerCost,
      license_quantity: isLicense ? quantity : 1,
      client_unit_price: isLicense ? clientUnitPrice : clientRate,
      provider_unit_cost: isLicense ? providerUnitCost : providerCost,
      scope_items: formData.scope_items.map((item) => item.trim()).filter(Boolean),
      status: formData.status,
      last_generated_billing_period: formData.last_generated_billing_period || null,
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = buildPayload();

    if (!payload.client_id && !payload.project_id) {
      toast.error('Debes seleccionar un cliente o proyecto');
      return;
    }
    if (!payload.service_name) {
      toast.error('El nombre del servicio es requerido');
      return;
    }

    try {
      if (editingSubscription) {
        await updateSubscription(getSubscriptionId(editingSubscription), payload);
        toast.success('Transacción recurrente actualizada');
      } else {
        await createSubscription(payload);
        toast.success('Transacción recurrente creada');
      }
      setIsFormOpen(false);
      setEditingSubscription(null);
      setFormData(emptyForm());
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'No se pudo guardar la transacción recurrente');
    }
  };

  const handleDelete = async (subscription) => {
    if (!window.confirm('¿Eliminar esta transacción recurrente?')) return;

    try {
      await deleteSubscription(getSubscriptionId(subscription));
      toast.success('Transacción recurrente eliminada');
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const handleGenerateQuotation = async (subscription) => {
    const blockedText = getGenerateQuotationBlockedText(subscription);
    if (blockedText) {
      toast.error(blockedText);
      return;
    }

    try {
      const { data } = await generateSubscriptionQuotation(getSubscriptionId(subscription));
      toast.success(`Cotización generada: ${data.quotation_number}`);
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'No se pudo generar la cotización');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[#E2B05E]" />
            <p className="text-[#9CA3AF]">Cargando transacciones recurrentes...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="font-['Outfit'] text-3xl font-semibold text-[#F3F4F6]">Transacciones recurrentes</h1>
            <p className="mt-1 text-[#9CA3AF]">Administra recurrencias y genera cotizaciones mensuales sin registrar cobros o pagos.</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            {subscriptions.length > 0 && (
              <Button
                variant="outline"
                onClick={() => import('../utils/exportUtils').then((mod) => mod.exportSubscriptionsToExcel(subscriptions))}
                className="border-[#242E42] text-[#9CA3AF]"
              >
                <DownloadSimple className="mr-2 h-4 w-4" />
                Excel
              </Button>
            )}
            <Button onClick={openCreateDialog} className="bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]">
              <Plus className="mr-2 h-5 w-5" />
              Nueva transacción
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <MetricCard icon={CheckCircle} label="Activas" value={metrics.activeCount} tone="emerald" />
          <MetricCard icon={TrendUp} label="Ingreso mensual" value={money(metrics.monthlyRevenue)} tone="gold" />
          <MetricCard icon={TrendDown} label="Costo mensual" value={money(metrics.monthlyCosts)} tone="red" />
          <MetricCard icon={Repeat} label="Margen mensual" value={money(metrics.monthlyMargin)} tone="emerald" />
        </div>

        <div className="valhalla-card p-4">
          <div className="flex flex-col gap-3 md:flex-row">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#9CA3AF]" />
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar por servicio, proveedor, cliente, periodo o cotización..."
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="active">Activas</SelectItem>
                <SelectItem value="paused">Pausadas</SelectItem>
                <SelectItem value="cancelled">Canceladas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="hidden overflow-hidden rounded-lg border border-[#242E42] bg-[#151B26] lg:block">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#242E42]">
              <thead className="bg-[#0B0E14]">
                <tr>
                  <TableHead>Servicio</TableHead>
                  <TableHead>Cliente / Proyecto</TableHead>
                  <TableHead>Frecuencia</TableHead>
                  <TableHead>Día</TableHead>
                  <TableHead>Ingreso</TableHead>
                  <TableHead>Costo</TableHead>
                  <TableHead>Último periodo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#242E42]">
                {filteredSubscriptions.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-[#9CA3AF]">
                      No hay transacciones recurrentes.
                    </td>
                  </tr>
                ) : (
                  filteredSubscriptions.map((subscription) => (
                    <tr key={getSubscriptionId(subscription)} className="hover:bg-[#0B0E14]/60">
                      <TableCell>
                        <div>
                          <p className="font-medium text-[#F3F4F6]">{subscription.service_name}</p>
                          <p className="text-xs text-[#9CA3AF]">{subscription.provider_name || 'Sin proveedor'}</p>
                          <Badge variant="outline" className="mt-2 border-[#242E42] text-[#9CA3AF]">
                            {transactionTypes.find((item) => item.value === subscription.transaction_type)?.label || 'Otro'}
                          </Badge>
                          {subscription.transaction_type === 'licencia' && (
                            <p className="mt-2 text-xs text-[#9CA3AF]">
                              {subscription.license_quantity || 1} lic. x {money(subscription.client_unit_price)}
                            </p>
                          )}
                          {(subscription.scope_items || []).length > 0 && (
                            <p className="mt-1 text-xs text-[#9CA3AF]">
                              {(subscription.scope_items || []).length} puntos de alcance
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-[#F3F4F6]">{subscription.client_name || subscription.project_name || 'Sin asignar'}</p>
                      </TableCell>
                      <TableCell>{frequencyLabels[subscription.billing_frequency] || subscription.billing_frequency}</TableCell>
                      <TableCell>{subscription.billing_day || '-'}</TableCell>
                      <TableCell className="font-semibold text-[#E2B05E]">{money(subscription.client_rate)}</TableCell>
                      <TableCell className="text-red-300">{money(subscription.provider_cost)}</TableCell>
                      <TableCell>
                        <div>
                          <p>{subscription.last_generated_billing_period || '-'}</p>
                          {subscription.last_generated_quotation_number && (
                            <p className="text-xs text-[#9CA3AF]">{subscription.last_generated_quotation_number}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={subscription.status} />
                      </TableCell>
	                      <TableCell>
	                        <div className="flex justify-end gap-2">
	                          <IconButton
	                            title={getGenerateQuotationBlockedText(subscription) || 'Generar cotización'}
	                            disabled={Boolean(getGenerateQuotationBlockedText(subscription))}
	                            onClick={() => handleGenerateQuotation(subscription)}
	                          >
	                            <FileText className="h-4 w-4" />
	                          </IconButton>
                          <IconButton title="Editar" onClick={() => openEditDialog(subscription)}>
                            <PencilSimple className="h-4 w-4" />
                          </IconButton>
                          <IconButton title="Eliminar" tone="danger" onClick={() => handleDelete(subscription)}>
                            <Trash className="h-4 w-4" />
                          </IconButton>
                        </div>
                      </TableCell>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid gap-4 lg:hidden">
          {filteredSubscriptions.length === 0 ? (
            <div className="valhalla-card p-8 text-center text-[#9CA3AF]">No hay transacciones recurrentes.</div>
          ) : (
            filteredSubscriptions.map((subscription) => (
              <div key={getSubscriptionId(subscription)} className="valhalla-card space-y-4 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#F3F4F6]">{subscription.service_name}</p>
                    <p className="text-sm text-[#9CA3AF]">{subscription.client_name || subscription.project_name || 'Sin asignar'}</p>
                  </div>
                  <StatusBadge status={subscription.status} />
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <MobileStat label="Tipo" value={transactionTypes.find((item) => item.value === subscription.transaction_type)?.label || 'Otro'} />
                  <MobileStat label="Frecuencia" value={frequencyLabels[subscription.billing_frequency] || subscription.billing_frequency} />
                  <MobileStat label="Día cobro" value={subscription.billing_day || '-'} />
                  <MobileStat label="Último periodo" value={subscription.last_generated_billing_period || '-'} />
                  <MobileStat label="Ingreso" value={money(subscription.client_rate)} highlight />
                  <MobileStat label="Costo" value={money(subscription.provider_cost)} danger />
                </div>
                {subscription.last_generated_quotation_number && (
                  <p className="text-xs text-[#9CA3AF]">Última cotización: {subscription.last_generated_quotation_number}</p>
                )}
                {subscription.transaction_type === 'licencia' && (
                  <p className="text-xs text-[#9CA3AF]">
                    {subscription.license_quantity || 1} licencias x {money(subscription.client_unit_price)} cliente / {money(subscription.provider_unit_cost)} proveedor
                  </p>
                )}
                {(subscription.scope_items || []).length > 0 && (
                  <p className="text-xs text-[#9CA3AF]">
                    Alcance configurado: {(subscription.scope_items || []).length} punto{(subscription.scope_items || []).length === 1 ? '' : 's'}
                  </p>
                )}
                <div className="grid grid-cols-3 gap-2">
	                  <Button
	                    size="sm"
	                    title={getGenerateQuotationBlockedText(subscription) || 'Generar cotización'}
	                    disabled={Boolean(getGenerateQuotationBlockedText(subscription))}
	                    onClick={() => handleGenerateQuotation(subscription)}
	                    className="bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A] disabled:cursor-not-allowed disabled:opacity-50"
	                  >
	                    Cotizar
	                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEditDialog(subscription)} className="border-[#242E42]">
                    Editar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(subscription)} className="border-red-500/50 text-red-300">
                    Borrar
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-[#242E42] bg-[#151B26] sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-[#F3F4F6]">
              {editingSubscription ? 'Editar transacción recurrente' : 'Nueva transacción recurrente'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormSection title="Cliente y asignación">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label="Cliente">
                  <Select value={formData.client_id} onValueChange={(value) => updateField('client_id', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin cliente</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client._id} value={client._id}>{client.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField label="Proyecto">
                  <Select value={formData.project_id} onValueChange={(value) => updateField('project_id', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar proyecto" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin proyecto</SelectItem>
                      {projects.map((project) => (
                        <SelectItem key={project._id} value={project._id}>{project.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              </div>
            </FormSection>

            <FormSection title="Recurrencia">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label="Servicio">
                  <Input value={formData.service_name} onChange={(event) => updateField('service_name', event.target.value)} required />
                </FormField>

                <FormField label="Proveedor">
                  <Input value={formData.provider_name} onChange={(event) => updateField('provider_name', event.target.value)} />
                </FormField>

                <FormField label="Tipo">
                  <Select value={formData.transaction_type} onValueChange={(value) => updateField('transaction_type', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {transactionTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField label="Frecuencia">
                  <Select value={formData.billing_frequency} onValueChange={(value) => updateField('billing_frequency', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Mensual</SelectItem>
                      <SelectItem value="quarterly">Trimestral</SelectItem>
                      <SelectItem value="biannual">Semestral</SelectItem>
                      <SelectItem value="annually">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField label="Fecha de inicio">
                  <Input type="date" value={formData.start_date} onChange={(event) => updateField('start_date', event.target.value)} required />
                </FormField>

                <FormField label="Día de cobro">
                  <Input type="number" min="1" max="31" value={formData.billing_day} onChange={(event) => updateField('billing_day', event.target.value)} />
                </FormField>
              </div>
            </FormSection>

            <FormSection title="Precio">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {isLicenseForm ? (
                  <>
                    <FormField label="Cantidad de licencias">
                      <Input type="number" min="1" value={formData.license_quantity} onChange={(event) => updateField('license_quantity', event.target.value)} />
                    </FormField>

                    <FormField label="Precio unitario cliente">
                      <Input type="number" step="0.01" min="0" value={formData.client_unit_price} onChange={(event) => updateField('client_unit_price', event.target.value)} />
                    </FormField>

                    <FormField label="Costo unitario proveedor">
                      <Input type="number" step="0.01" min="0" value={formData.provider_unit_cost} onChange={(event) => updateField('provider_unit_cost', event.target.value)} />
                    </FormField>

                    <div className="rounded-lg border border-[#242E42] bg-[#0B0E14] p-3">
                      <p className="text-xs text-[#9CA3AF]">Totales calculados</p>
                      <p className="mt-1 text-sm text-[#F3F4F6]">
                        Cliente: <span className="font-semibold text-[#E2B05E]">{money(licenseClientTotal)}</span>
                      </p>
                      <p className="text-sm text-[#F3F4F6]">
                        Proveedor: <span className="font-semibold text-red-300">{money(licenseProviderTotal)}</span>
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <FormField label="Monto cliente">
                      <Input type="number" step="0.01" min="0" value={formData.client_rate} onChange={(event) => updateField('client_rate', event.target.value)} />
                    </FormField>

                    <FormField label="Costo proveedor">
                      <Input type="number" step="0.01" min="0" value={formData.provider_cost} onChange={(event) => updateField('provider_cost', event.target.value)} />
                    </FormField>
                  </>
                )}
              </div>
            </FormSection>

            <FormSection title="Alcance de servicio">
              <FormField label="Notas visibles">
                <Textarea
                  value={formData.description}
                  onChange={(event) => updateField('description', event.target.value)}
                  rows={3}
                  placeholder="Texto adicional visible en la cotización..."
                />
              </FormField>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-[#F3F4F6]">Puntos del alcance</p>
                    <p className="text-xs text-[#9CA3AF]">Se agregan como bullets dentro de la línea de la cotización.</p>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={addScopeItem} className="border-[#242E42]">
                    <Plus className="mr-1 h-4 w-4" />
                    Punto
                  </Button>
                </div>

                {formData.scope_items.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-[#242E42] bg-[#0B0E14] p-4 text-sm text-[#9CA3AF]">
                    Sin puntos configurados.
                  </div>
                ) : (
                  formData.scope_items.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={item}
                        onChange={(event) => updateScopeItem(index, event.target.value)}
                        placeholder={`Punto ${index + 1}`}
                      />
                      <Button type="button" variant="outline" onClick={() => removeScopeItem(index)} className="border-red-500/50 text-red-300">
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </FormSection>

            <FormSection title="Tracking">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label="Estado">
                  <Select value={formData.status} onValueChange={(value) => updateField('status', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Activa</SelectItem>
                      <SelectItem value="paused">Pausada</SelectItem>
                      <SelectItem value="cancelled">Cancelada</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField label="Último periodo generado">
                  <Input type="month" value={formData.last_generated_billing_period} onChange={(event) => updateField('last_generated_billing_period', event.target.value)} />
                </FormField>
              </div>
            </FormSection>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)} className="border-[#242E42]">
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]">
                {editingSubscription ? 'Guardar cambios' : 'Crear transacción'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function MetricCard({ icon: Icon, label, value, tone }) {
  const toneClass = {
    emerald: 'text-emerald-400',
    gold: 'text-[#E2B05E]',
    red: 'text-red-400',
  }[tone];

  return (
    <div className="valhalla-card p-5">
      <div className="flex items-center gap-3">
        <Icon className={`h-8 w-8 ${toneClass}`} weight="duotone" />
        <div>
          <p className="text-sm text-[#9CA3AF]">{label}</p>
          <p className={`text-2xl font-bold ${toneClass || 'text-[#F3F4F6]'}`}>{value}</p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    active: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
    paused: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300',
    cancelled: 'border-red-500/40 bg-red-500/10 text-red-300',
  };

  return (
    <Badge className={styles[status] || 'border-[#242E42] bg-[#0B0E14] text-[#9CA3AF]'}>
      {statusLabels[status] || status || 'Sin estado'}
    </Badge>
  );
}

function TableHead({ children, className = '' }) {
  return <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] ${className}`}>{children}</th>;
}

function TableCell({ children, className = '' }) {
  return <td className={`whitespace-nowrap px-6 py-4 text-sm text-[#D1D5DB] ${className}`}>{children}</td>;
}

function IconButton({ children, title, onClick, tone, disabled = false }) {
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`${tone === 'danger' ? 'text-red-300 hover:text-red-200' : 'text-[#9CA3AF] hover:text-[#F3F4F6]'} disabled:cursor-not-allowed disabled:opacity-50`}
    >
      {children}
    </Button>
  );
}

function FormField({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-[#9CA3AF]">{label}</Label>
      {children}
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <section className="space-y-4 rounded-lg border border-[#242E42] bg-[#0B0E14]/55 p-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-[#E2B05E]">{title}</h3>
      {children}
    </section>
  );
}

function MobileStat({ label, value, highlight, danger }) {
  return (
    <div className="rounded-lg border border-[#242E42] bg-[#0B0E14] p-3">
      <p className="text-xs text-[#9CA3AF]">{label}</p>
      <p className={`mt-1 font-semibold ${highlight ? 'text-[#E2B05E]' : danger ? 'text-red-300' : 'text-[#F3F4F6]'}`}>{value}</p>
    </div>
  );
}
